/**
 * Phase 342 wave 4 task 4.1 — the ONE portal-side mapping from a reporting metric family
 * to the page that renders it.
 *
 * <p>The engine answers "which families is this tenant configured for" in its own generic
 * vocabulary (`weekly-sales-activity`, `sales-forecast`, `job-status`). Those strings are
 * protocol: they name fact rows and query parameters, and they are invisible to a client.
 * This file is where protocol becomes product — the single place that decides a family's
 * ROUTE and its CLIENT-FACING LABEL.</p>
 *
 * <p>Phase 530 wave 4 task 4.1 (#1939): a family need not be a REPORT. The engine's families
 * answer grew a second, additive derivation arm that advertises a LIVE VIEW of workflow state
 * (`job-status`) for a tenant whose active declaration carries a `status_view` block. Nothing
 * in this file's mechanism changes for it — one row, one route, one label, and the same
 * "configured × has a page" intersection below — which is the property the derivation was
 * built to have.</p>
 *
 * <h3>Why the labels are what they are</h3>
 * <p>"Sales Activity" and "Vacancies" are the client's own words for these two reports
 * (the pinned vocabulary decision behind #1163: the client uses a Sales Activity tab and a
 * Vacancies tab, and the platform mirrors their language rather than its own). They are
 * landed correct-first here so that #1163 has nothing to rename — it still owns the
 * vocabulary fence test and any residual sweep, but not a migration of this file.</p>
 *
 * <h3>Why a family with no entry renders nothing</h3>
 * <p>A family arrives from the API the moment a tenant's workflow is configured for it —
 * which can happen before this app has a page to show it (that is the WHOLE POINT of
 * deriving nav from configuration: a new family appears with no portal deploy). Linking to
 * a route that does not exist would turn that good property into a broken link, so an
 * unregistered family is dropped and announced on the dev console instead: a family needs a
 * page before it needs a nav entry.</p>
 */

/**
 * The client's name for each report — the ONE spelling of each, in the whole bundle.
 *
 * <p>Phase 336 task 3.3 (#1163) points every surface the portal controls at these two
 * constants: the nav entry below, the page's heading, and the browser tab
 * (`usePageTitle`). Not because a string is expensive to repeat, but because three copies
 * of a client's own vocabulary is three places for it to drift — and the failure mode is
 * silent and client-visible (a rail that says one thing and a heading that says another).
 * A rename is now one edit, and `test/vocabulary-fence.test.ts` guards what a rename is
 * FROM.</p>
 */
export const SALES_ACTIVITY_LABEL = "Sales Activity";
export const VACANCIES_LABEL = "Vacancies";

/**
 * Phase 530 wave 4 task 4.1 (#1939) — the client's name for the onboarding status board.
 *
 * <p>Same rule as the two above: ONE spelling, read by the nav entry, the page heading and the
 * browser tab alike. "Onboarding" is the firm's own word for the process these files are in —
 * not the platform's (`job-status`, `status_view`, `workflow_jobs`), which is machine
 * vocabulary and stops at the wire.</p>
 *
 * <p>It is a CONSTANT here rather than a declared per-tenant string, deliberately (§ Out of
 * scope): a `status_view.board_label` would be a `packages/workflow-schema` change for copy
 * that reads correctly for every tenant today.</p>
 */
export const ONBOARDING_LABEL = "Onboarding";

/**
 * Walk 3 — the wire family that means "this tenant has an onboarding board".
 *
 * <p>The engine advertises it on the families read for a tenant whose ACTIVE declaration carries
 * a top-level `status_view` block (ReportFamilyRegistry.LiveViewEntries). That is the ONE signal
 * the portal uses for "has onboarding": the nav entry below is gated on it, and so is the board
 * itself (`FilesBoardPage` shows "Onboarding isn't set up for …" rather than asking the engine for
 * a board it will refuse). No second endpoint, no portal-side guess.</p>
 */
export const ONBOARDING_FAMILY = "job-status";

/**
 * Whether a tenant's families answer includes the onboarding board. `undefined` while the read is
 * in flight — "not known yet" is not "no", and a page must not flash the not-set-up state at a
 * tenant whose answer is on its way.
 */
export function hasOnboarding(
  families: readonly ConfiguredFamilyRow[] | undefined,
): boolean | undefined {
  if (families === undefined) return undefined;
  return families.some((f) => f.family === ONBOARDING_FAMILY);
}

/**
 * ADR-122 — the staff page where a firm rewords the emails its clients receive. Not a report family:
 * the engine's list of emails the signed-in person may reword decides whether the entry shows
 * (portalQueries `usePortalNavEntries`), and it is appended after every report and board so `/`
 * lands where it always did. ONE spelling of the label, read by the nav, the heading and the tab.
 */
export const EMAILS_LABEL = "Emails";
export const EMAILS_ROUTE = "/emails";

/** A family the portal can actually show, and where. */
export interface PortalFamilyEntry {
  /**
   * The engine's family wire value — matched by EXACT equality against
   * `ReportingFamiliesResponse.families[].family`. Never parsed, never normalised.
   */
  family: string;
  /** The portal route that renders this family. */
  route: string;
  /** The client-facing nav label. The client's vocabulary, not the platform's. */
  label: string;
}

/**
 * Every family this app has a page for, in the order the nav presents them.
 *
 * <p>Order lives HERE rather than being taken from the API response: the server's order is
 * an implementation detail of its own registry, and the nav should not reshuffle because a
 * workflow was redeployed. Adding a family is one row plus its page and route.</p>
 */
export const PORTAL_FAMILY_REGISTRY: readonly PortalFamilyEntry[] = [
  {
    // Written by run_report{report: weekly-sales-activity} — the report key doubles as
    // the family for that report (ReportingFactStore files it under the key).
    family: "weekly-sales-activity",
    route: "/sales-activity",
    label: SALES_ACTIVITY_LABEL,
  },
  {
    // Written by TWO invocations, both filing under the 'sales-forecast' family
    // (ForecastFactWriteThrough.SalesForecastFamily):
    //   • the combined Monday run_report{report: weekly-sales-activity} run, while the
    //     tenant declares tenant_config.reporting.forecast (ADR-088) — which is also the
    //     condition the engine's ReportFamilyRegistry gates that producer on, so this nav
    //     entry appears exactly when there are rows behind it;
    //   • the generic standalone run_forecast_report{report: forecast-revenue}, ungated.
    // An activity-only tenant (no forecast block, no standalone forecast workflow) is
    // therefore shown Sales Activity alone, BY DESIGN.
    family: "sales-forecast",
    route: "/vacancies",
    label: VACANCIES_LABEL,
  },
  {
    // Phase 530 wave 4 task 4.1 (#1939) — NOT a report family, and the difference matters when
    // reading this list. The two above are written by a scheduled report tool into the
    // reporting fact store; this one is a LIVE VIEW of workflow state, advertised by the engine
    // when the tenant's ACTIVE declaration carries phase 528's top-level `status_view` block
    // (ReportFamilyRegistry.LiveViewEntries). Same endpoint, same wire identity, same one-row
    // registration here — a different producer behind it.
    //
    // So it appears for a tenant whose onboarding workflow declares a status view, and for
    // nobody else: a tenant with no such declaration gets a byte-identical families answer to
    // the one it got before this phase, and therefore an unchanged nav.
    //
    // LAST in the list on purpose. Registry order is nav order (see above) and it also decides
    // where `/` lands, because PortalLanding redirects to entries[0] — so appending keeps every
    // existing tenant landing exactly where they landed yesterday. It is also the engine's own
    // presentation order for the three families, which keeps the rail and the API in step.
    family: ONBOARDING_FAMILY,
    route: "/onboarding",
    label: ONBOARDING_LABEL,
  },
];

// ── #2508 — one board per status_view declaration (contract A6) ───────────────────────────
//
// The engine now sends ONE `job-status` row per declaration carrying a `status_view` block:
// `{ family: "job-status", report_key: <workflow_id>, label: <board_label or "Onboarding"> }`. So a
// tenant with an onboarding workflow and a client-review workflow has two boards, each its own nav
// entry. The registry row above stays the TEMPLATE for that family (its order in the rail, and the
// `/onboarding` route); the rows themselves are expanded here.
//
// Routes: every board lives at `/boards/<workflow_id>`, and the tenant's DEFAULT board — the FIRST
// job-status row the engine sends — is also `/onboarding`, the address every existing link and
// bookmark already uses. The default board's nav entry keeps `/onboarding`, so nothing a staffer
// knows moves.

/** The address of a board that is not the tenant's default one. */
export function boardRoute(workflowId: string): string {
  return `/boards/${encodeURIComponent(workflowId)}`;
}

/** One status board a tenant has, and where it lives. */
export interface BoardEntry {
  /**
   * The declaration whose board this is, sent as `?workflow_id=` on every board read. `null` for
   * an engine older than #2508, whose single row names no workflow (its `report_key` is the family
   * name): that board's read lets the engine choose the spine, exactly as before.
   */
  workflowId: string | null;
  /** The board's label: the declared `board_label`, else "Onboarding". */
  label: string;
  /** Where the board renders. The default board is `/onboarding`. */
  route: string;
  /** The FIRST job-status row: the board `/onboarding` shows. */
  isDefault: boolean;
}

/**
 * A board's Import page address, or null for a board that names no workflow (an engine older than
 * #2508). Whether the board TAKES an import is the board read's answer (`has_import` on the
 * job-status envelope), not the families row's: the board page offers the link, and the Import page
 * checks the same flag before it offers a file picker.
 */
export function importRouteFor(board: BoardEntry): string | null {
  return board.workflowId === null ? null : `${boardRoute(board.workflowId)}/import`;
}

/**
 * Every board the tenant has, in the engine's order, one per distinct workflow. Empty while the
 * families read is in flight and for a tenant with no status board.
 */
export function boardsFor(families: readonly ConfiguredFamilyRow[] | undefined): BoardEntry[] {
  if (!families) return [];
  const boards: BoardEntry[] = [];
  const seen = new Set<string>();
  for (const row of families) {
    if (row.family !== ONBOARDING_FAMILY) continue;
    const legacy = row.report_key === undefined || row.report_key === ONBOARDING_FAMILY;
    const workflowId = legacy ? null : (row.report_key as string);
    const identity = workflowId ?? "";
    if (seen.has(identity)) continue;
    seen.add(identity);
    const isDefault = boards.length === 0;
    const label = typeof row.label === "string" && row.label.trim() !== "" ? row.label : ONBOARDING_LABEL;
    boards.push({
      workflowId,
      label,
      route: isDefault || workflowId === null ? "/onboarding" : boardRoute(workflowId),
      isDefault,
    });
  }
  return boards;
}

/** The board `/onboarding` shows: the first one. `undefined` when there is none. */
export function defaultBoard(families: readonly ConfiguredFamilyRow[] | undefined): BoardEntry | undefined {
  return boardsFor(families)[0];
}

/** The board for one workflow id from a `/boards/<id>` address, or `undefined` when the tenant has none. */
export function boardFor(
  families: readonly ConfiguredFamilyRow[] | undefined,
  workflowId: string,
): BoardEntry | undefined {
  return boardsFor(families).find((board) => board.workflowId === workflowId);
}

/** The Import page's nav label: "Import clients" for a board called "Clients". */
export function importLabel(board: BoardEntry): string {
  return `Import ${board.label.toLowerCase()}`;
}

/** The families-row fields the nav reads. */
export interface ConfiguredFamilyRow {
  family: string;
  report_key?: string;
  label?: string;
}

/** ADR-122 — the Emails nav entry. Its `family` is a marker, never sent to the engine. */
export const EMAILS_ENTRY: PortalFamilyEntry = { family: "staff:emails", route: EMAILS_ROUTE, label: EMAILS_LABEL };

/** The entry for a metric family, or `undefined` when this app has no page for it. */
export function resolveFamily(family: string): PortalFamilyEntry | undefined {
  return PORTAL_FAMILY_REGISTRY.find((entry) => entry.family === family);
}

/**
 * The nav entries for one tenant: the INTERSECTION of what they have configured with what
 * this app can render, in registry order.
 *
 * <p>Implemented as a filter over the registry rather than a map over the response, which
 * gives three properties for free: registry order wins, duplicates in the response collapse,
 * and a family with no page cannot produce a nav item.</p>
 *
 * @param families the `families` array from `GET /reporting/families`, or `undefined`
 *                 while the read is still in flight.
 */
export function navEntriesFor(
  families: readonly ConfiguredFamilyRow[] | undefined,
): PortalFamilyEntry[] {
  if (!families) return [];

  const configured = new Set(families.map((f) => f.family));
  for (const family of configured) {
    if (!resolveFamily(family)) warnUnregisteredFamily(family);
  }

  // #2508: the job-status template expands into one entry per board, in the engine's order; every
  // other family is its single registry row. A board's Import page is reached from the board itself
  // (the board read says whether it takes one), not from the rail.
  return PORTAL_FAMILY_REGISTRY.filter((entry) => configured.has(entry.family)).flatMap((entry) => {
    if (entry.family !== ONBOARDING_FAMILY) return [entry];
    return boardsFor(families).map(
      (board): PortalFamilyEntry => ({ family: entry.family, route: board.route, label: board.label }),
    );
  });
}

/**
 * Announce a configured family this app cannot show. A dev-console warning rather than a
 * thrown error or a rendered placeholder: the tenant's OTHER reports must still work, and
 * the person who needs to know is a developer adding the missing page — not the client.
 */
function warnUnregisteredFamily(family: string): void {
  // eslint-disable-next-line no-console
  console.warn(
    `client-portal: family "${family}" is configured for this tenant but no page ` +
      "is registered for it, so it is not shown. Add it to PORTAL_FAMILY_REGISTRY " +
      "(src/nav/familyRegistry.ts) once its page and route exist.",
  );
}
